// Extraído de PlanCuotasModal (acción "Ajustar fecha", handleGuardarFecha) — Fase 1 de "Futuros:
// reagendar" (2026-09-23). Mismo comportamiento exacto, solo se movió el cuerpo de la función a un
// util standalone para que PlanCuotasModal Y el despachador nuevo `reagendarEntryFuturo` llamen a
// LA MISMA función (10.1) en vez de mantener dos copias. Cero cambio de lógica.
//
// Cubre: cobro (facturas_sii_emitidas.plazocobro) y pago — SII recibida (facturas_sii_recibidas.
// plazopago), externa (facturas_externas.plazopago, vía documento_id) o boleta de honorarios
// (boletas_honorarios.plazopago, vía factura.boleta_id — Fase 4 de "Futuros: reagendar", 2026-09-25,
// columna nueva; antes de esa fase la boleta quedaba fuera del despachador porque no tenía ninguna
// columna de fecha de pago).
//
// Devuelve `{ error: string }` en cualquier fallo (nunca lanza para los casos de validación de
// negocio ya presentes en el código original — solo re-lanza errores de red/Postgres inesperados,
// igual que antes) o `{ success: true }`. El caller decide cómo mostrar el error (toast, etc.) — la
// UI queda fuera de este util a propósito.
import { resolverDocumentoFiscalId } from "./documentosFiscales";
import { eliminarPlanPagos, eliminarPlanPagosPorDocumento } from "./planCobros";
import { client } from "./supabase";

/**
 * @param {Object} params
 * @param {Object} params.factura - la entry/factura tal como la usa PlanCuotasModal (id, es_externa,
 *   es_boleta, boleta_id, moneda, ...).
 * @param {"cobro"|"pago"} params.tipo
 * @param {string} params.nuevaFecha - "YYYY-MM-DD"
 */
export const guardarFechaFactura = async ({ factura, tipo, nuevaFecha }) => {
  const esCobro = tipo === "cobro";
  const esExterna = !!factura?.es_externa;
  const esBoleta = !!factura?.es_boleta;
  const esDocId = esExterna || esBoleta;
  const origenId = esBoleta ? factura?.boleta_id : factura?.id;
  const docTipo = esBoleta
    ? "boleta_honorario"
    : esExterna
      ? "factura_externa"
      : esCobro
        ? "sii_emitida"
        : "sii_recibida";

  const campo = esCobro ? "plazocobro" : "plazopago";
  // Externa → facturas_externas.plazopago. Boleta → boletas_honorarios.plazopago (Fase 4 de "Futuros:
  // reagendar", 2026-09-25, columna nueva — migración 20260925c, aplicada). SII → emitidas/recibidas
  // según cobro/pago. Boleta nunca es "cobro" (siempre pago), así que su rama no compite con esCobro.
  //
  // Fix (2026-09-25, descubierto por lectura al preparar esta fase): ANTES de este fix, `esBoleta`
  // nunca se reflejaba acá — una boleta que llegara a este punto (p.ej. desde PlanCuotasModal
  // "Ajustar fecha" al convertir un plan de boleta a pago único) caía al fallback
  // "facturas_sii_recibidas", la tabla EQUIVOCADA. Nunca se ejerció en la práctica: antes de la Fase 4
  // ninguna boleta SIN plan tenía fecha que ajustar (sin columna plazopago), así que "Ajustar fecha"
  // no era una acción disponible para boletas por esa vía. El resto de la función YA era boleta-aware
  // (`origenId`/`docTipo` arriba, para el borrado del plan) — este fix completa la última pieza.
  const tabla = esBoleta
    ? "boletas_honorarios"
    : esExterna
      ? "facturas_externas"
      : esCobro
        ? "facturas_sii_emitidas"
        : "facturas_sii_recibidas";

  // Pago (SII recibida O externa): "Ajustar fecha" (pago único) y "En cuotas" son modos
  // EXCLUYENTES. Pasar a pago único debe BORRAR el plan de cuotas: si no, FlujoCajaV2 sigue
  // mostrando las cuotas (su rama tiene precedencia sobre plazopago mientras existan filas).
  // Cobro no tiene este plan. Fase 5a: la externa también cuotea → su plan cuelga de documento_id.
  if (!esCobro) {
    const docIdDoc = esDocId ? await resolverDocumentoFiscalId(docTipo, origenId) : null;
    // Externa/boleta sin documento resoluble: no hay plan que borrar (defensivo).
    if (!esDocId || docIdDoc != null) {
      // Ground truth desde DB (el state puede no estar cargado en el tab "fecha").
      let query = client.from("plan_pagos_facturas").select("id, estado, movimiento_bancario_id, numero_cuota");
      query = esDocId ? query.eq("documento_id", docIdDoc) : query.eq("factura_sii_recibida_id", factura.id);
      const { data: cuotasActuales, error: errCuotas } = await query;
      if (errCuotas) {
        return { error: `Error al verificar el plan de cuotas: ${errCuotas.message}` };
      }
      // Guarda (10.64 / 10.103): NUNCA borrar una cuota pagada o vinculada a un movimiento — sostiene
      // un vínculo con caja (la vinculación deja estado='pagado' + movimiento_bancario_id, y apunta
      // movimientos_bancarios.plan_pago_cuota_id a la cuota). En ese caso no se convierte a pago único.
      // Fase 2d (2026-09-24, cierre de fisuras de la auditoría de 2b+2c, fisura e): lista BLANCA, no
      // negra — antes bloqueaba solo `estado === "pagado" || estado === "cobrado"` (dos valores
      // conocidos), lo que dejaba pasar cualquier estado FUTURO/desconocido que este código todavía no
      // conozca (mismo patrón que ya se cerró en `esCuotaReagendable`/cuotaReagendable.js). Ahora
      // bloquea si CUALQUIER cuota tiene `estado !== "pendiente"`, sin importar cuál sea ese valor.
      const bloqueada = (cuotasActuales || []).some(
        (c) => c.estado !== "pendiente" || c.movimiento_bancario_id != null
      );
      if (bloqueada) {
        return {
          error: "La factura tiene cuotas pagadas o vinculadas a un movimiento; no se puede convertir a pago único.",
        };
      }
      // Fase 2b (2026-09-24, cierre de fisuras de la auditoría independiente, fisura d), ENDURECIDO en
      // Fase 2d (2026-09-24, cierre de fisuras de la auditoría de 2b+2c, fisura a — criterio único
      // entre pantalla y RPC): además de estado/movimiento_bancario_id de arriba (el flag escalar),
      // consultar el libro `aplicaciones_saldo` — SOLO LECTURA, prohibido escribir ahí desde este
      // archivo (bloqueo vigente de la Fase 3 del cierre de fuente única) — por si CUALQUIER cuota de
      // este plan tiene AL MENOS una fila ya aplicada que el escalar `estado` todavía no refleja (mismo
      // caso que motivó `esCuotaReagendable`/cuotaReagendable.js: cuota 52/documento 8735, 1.000.000 de
      // 1.415.326). EXISTS puro, sin tolerancia — MISMO criterio (no un tercer criterio propio, 10.1)
      // que usa `esCuotaReagendable` (cuotaReagendable.js) y la RPC `reagendar_cuota_factura`: antes de
      // Fase 2d esto sumaba `monto_documento` y comparaba contra `TOLERANCIA_MISMA_MONEDA`, lo que podía
      // divergir del EXISTS de la RPC en el borde exacto de la tolerancia — cerrado.
      if ((cuotasActuales || []).length > 0) {
        const cuotaIds = cuotasActuales.map((c) => c.id);
        const { data: eventosLibro, error: errLibro } = await client
          .from("aplicaciones_saldo")
          .select("cuota_id")
          .eq("documento_tipo", docTipo)
          .in("cuota_id", cuotaIds);
        if (errLibro) {
          return { error: `Error al verificar el libro de aplicaciones: ${errLibro.message}` };
        }
        const cuotaIdsConEventos = new Set((eventosLibro || []).map((e) => e.cuota_id));
        const cuotaConAplicacion = cuotasActuales.find((c) => cuotaIdsConEventos.has(c.id));
        if (cuotaConAplicacion) {
          return {
            error: `La cuota ${cuotaConAplicacion.numero_cuota ?? cuotaConAplicacion.id} tiene una aplicación registrada en el libro de saldos — no se puede convertir a pago único.`,
          };
        }
      }
      // Solo se llega acá con todas las cuotas pendientes, sin vínculo bancario y sin aplicación en el
      // libro. eliminarPlanPagos* tira throw si PostgREST devuelve error → lo propaga el caller (mismo
      // catch que antes en el modal).
      //
      // Fase 2d (2026-09-24, cierre de fisuras de la auditoría de 2b+2c, fisura e): el DELETE que hacen
      // eliminarPlanPagos/eliminarPlanPagosPorDocumento ya NO es incondicional — llevan el mismo filtro
      // `.eq("estado","pendiente").is("movimiento_bancario_id",null)` (candado en la query, no solo en
      // el chequeo previo de JS) y devuelven las filas REALMENTE borradas. Acá se verifica que la
      // CANTIDAD borrada coincide con `cuotasActuales.length` (la cantidad esperada, cargada arriba
      // ANTES de decidir borrar) — si no coincide, alguien pagó/vinculó una cuota justo en la ventana
      // entre el chequeo de arriba y este DELETE (misma clase de carrera que motivó el UPDATE
      // condicional de la Fase 2b/el candado de fila real de la RPC). En ese caso se corta ACÁ, con un
      // error explícito, y se PROPAGA sin llegar al UPDATE de `plazopago`/`plazocobro` de abajo — un
      // borrado parcial nunca debe quedar con la factura marcada "pago único" si el plan no se pudo
      // limpiar por completo (10.177: nunca un estado a medias silencioso).
      if ((cuotasActuales || []).length > 0) {
        const borradas = await (esDocId ? eliminarPlanPagosPorDocumento(docIdDoc) : eliminarPlanPagos(factura.id));
        if ((borradas || []).length !== cuotasActuales.length) {
          return {
            error:
              `Se esperaba borrar ${cuotasActuales.length} cuota(s) del plan pero se borraron ` +
              `${(borradas || []).length} — alguna cuota cambió de estado mientras se procesaba el pedido. ` +
              "No se convirtió a pago único, volvé a intentar.",
          };
        }
      }
    }
  }

  // Fallo silencioso (10.177): PostgREST no tira throw en update → hay que chequear el {error}.
  // Boleta: el `id` de la ENTRY (`factura.id`) es el string sintético `"boleta-<id>"` que arma el
  // mapper (key de React/folio) — el id REAL de `boletas_honorarios` es `factura.boleta_id` (numérico,
  // ya viaja en `camposModalBoleta` desde Fase 5b). SII/externa siguen usando `factura.id` tal cual: su
  // mapper hace spread de la fila real (`...f`), sin wrapping sintético.
  const { error: errUpd } = await client
    .from(tabla)
    .update({ [campo]: nuevaFecha })
    .eq("id", esBoleta ? factura.boleta_id : factura.id);
  if (errUpd) {
    return { error: `Error al guardar la fecha: ${errUpd.message}` };
  }
  return { success: true };
};
